import { Component, OnInit } from '@angular/core';
import { IFormGroup } from '@shared-libs/interfaces';
import { IEventType } from '@internal-models/event-type.model';
import { EventTypeProvider } from '@internal-providers/event-type.provider';
import {
	CustomIntervalType,
	EventAttendanceType,
	EventInterval,
	EventLocationType,
	IEvent,
} from '@internal-models/event.model';
import { EventFormFactory, IEventForm } from '@internal-factories/forms/event-form.factory';
import {
	Action,
	DigibankActivity,
	DigibankObjective,
	NavigationDataKey,
	NavigationType,
	UserPermissions,
} from '@shared-libs/enums';
import { NavigationService } from '@shared-services/navigation.service';
import { EventTypeComponent } from '@internal-components/administration-components/event-type-components/event-type-component/event-type.component';
import {
	formIsValid,
	translate,
	userWantsRecurringActionOnDeletingEvent,
	userWantsRecurringActionOnEvent,
} from '@shared-libs/helpers';
import { ILocation } from '@internal-models/location.model';
import { LocationProvider } from '@internal-providers/location.provider';
import { EventProvider } from '@internal-providers/event.provider';
import { LocationComponent } from '@internal-components/administration-components/locations-components/location-component/location.component';
import { IEmployee } from '@internal-models/employee.model';
import { EmployeeProvider } from '@internal-providers/employee.provider';
import { IDigibankAgency } from '@internal-models/digibank-agency.model';
import { DigibankAgencyProvider } from '@internal-providers/digibank-agency.provider';
import { DigibankAgencyComponent } from '@internal-components/administration-components/digibank-agency-components/digibank-agency-component/digibank-agency.component';
import { NavigationDataManager } from '@shared-managers/navigation-data.manager';
import { format, getDate, set } from 'date-fns';
import { nlBE } from 'date-fns/locale';
import { LessonComponent } from '../lesson-component/lesson.component';
import { orderBy } from 'lodash';
import { ILesson } from '@internal-models/lesson.model';
import { ValidationService } from '@shared-services/validation.service';
import { AppModule } from '@app/app.module';
import { UserDetailsManager } from '@shared-managers/user-details.manager';
import { IUserDetails } from '@shared-models/user.model';
import { IUiTDatabaseLabel } from '@internal-models/uit-database-label.model';
import { UiTDatabaseLabelProvider } from '@internal-providers/uit-database-label.provider';
import { CommunicationMessageType, ICommunicationTemplate } from '@internal-models/communication-template.model';
import { CommunicationTemplateProvider } from '@internal-providers/communication-templates.provider';

@Component({
	selector: 'event-component',
	templateUrl: 'event.component.html',
	styleUrls: ['./event.component.scss'],
})
export class EventComponent implements OnInit {
	public eventForm: IFormGroup<IEventForm>;
	public event: IEvent;
	public action: Action;
	public locations: Array<ILocation>;
	public eventTypes: Array<IEventType>;
	public employees: Array<IEmployee>;
	public agencies: Array<IDigibankAgency>;
	public customCommunicationTemplates: Array<ICommunicationTemplate>;
	public eventIntervals: Array<{ value: string; label: string }>;
	public customIntervalTypes: Array<{ value: string; label: string }>;
	public eventAttendanceTypes: Array<{ value: string; label: string }>;
	public isLoanLocationOptions: Array<{ value: boolean; label: string }>;
	public digibankActivities: Array<{ value: DigibankActivity; label: string }>;
	public digibankObjectives: Array<{ value: DigibankObjective; label: string }>;
	public eventLocationTypes: Array<{ value: EventLocationType; label: string }>;
	public uitDatabaseLabels: Array<IUiTDatabaseLabel>;
	public lessons: Array<ILesson>;

	public readonly Action: typeof Action = Action;
	public readonly EventInterval: typeof EventInterval = EventInterval;
	public readonly EventAttendanceType: typeof EventAttendanceType = EventAttendanceType;
	public readonly EventLocationType: typeof EventLocationType = EventLocationType;
	public readonly UserPermissions: typeof UserPermissions = UserPermissions;
	private selectedDate: Date;

	public readonly colors: Array<{ hex: string; name: string }> = [
		{ hex: '#DBC9E9', name: 'Languid Lavender' },
		{ hex: '#C9F4FB', name: 'Water' },
		{ hex: '#C9EFCB', name: 'Tea Green' },
		{ hex: '#FFFAC9', name: 'Lemon Chiffon' },
		{ hex: '#FFE7C9', name: 'Lumber' },
		{ hex: '#FDC9C9', name: 'Light Red' },
		{ hex: '#BECCE8', name: "Crayola's Periwinkle" },
		{ hex: '#CFE7E8', name: 'Azureish White' },
		{ hex: '#F9F2EE', name: 'Isabelline' },
		{ hex: '#F5E4E7', name: 'Piggy Pink' },
	];

	constructor(
		private readonly eventTypeProvider: EventTypeProvider,
		private readonly locationProvider: LocationProvider,
		private readonly eventProvider: EventProvider,
		private readonly employeeProvider: EmployeeProvider,
		private readonly digibankAgencyProvider: DigibankAgencyProvider,
		private readonly navigationService: NavigationService,
		private readonly navigationDataManager: NavigationDataManager,
		private readonly userDetailsManager: UserDetailsManager,
		private readonly uitDatabaseLabelProvider: UiTDatabaseLabelProvider,
		private readonly communicationTemplateProvider: CommunicationTemplateProvider
	) {}

	public ngOnInit(): void {
		this.initializeForm();
		this.getEmployees();
		this.getLocations();
		this.getEventTypes();
		this.getEventIntervals();
		this.getCustomIntervalTypes();
		this.getDigibankAgencies();
		this.getEventAttendanceTypes();
		this.getIsLoanLocationOptions();
		this.getDigibankActivities();
		this.getDigibankObjectives();
		this.getEventLocationTypes();
		this.getUitDatabaseLabels();
	}

	public async createEvent(): Promise<void> {
		if (formIsValid(this.eventForm, EventComponent)) {
			const event = EventFormFactory.getValue(this.eventForm);
			await this.eventProvider.create(event as IEvent);
			this.navigationService.navigateBack({ action: Action.CREATE });
		}
	}

	public async updateEvent(): Promise<void> {
		if (formIsValid(this.eventForm, EventComponent)) {
			const event = EventFormFactory.getValue(this.eventForm);
			let isRecurring = null;

			if (this.event.interval === EventInterval.OneTime && event.interval !== EventInterval.OneTime) {
				isRecurring = true;
			} else {
				isRecurring = await userWantsRecurringActionOnEvent(this.event.interval, Action.UPDATE);
			}

			if (isRecurring !== null) {
				const updatedEvent = await this.eventProvider.update(
					this.event.id,
					event as IEvent,
					this.event.CalendarEvent,
					isRecurring
				);
				this.navigationService.navigateBack({
					action: Action.UPDATE,
					event: updatedEvent,
					calendarEvent: { ...updatedEvent.CalendarEvents[0], Event: updatedEvent },
				});
			}
		}
	}

	public async deleteEvent(): Promise<void> {
		let isRecurring: boolean;
		if (this.event.attendanceType !== EventAttendanceType.Lessons) {
			isRecurring = await userWantsRecurringActionOnDeletingEvent(this.event.interval);
		} else {
			const validationService: ValidationService = AppModule.injector.get(ValidationService);
			isRecurring = await validationService.actionIsConfirmed('LESSONS_ALL');
		}

		if (isRecurring !== null) {
			await this.eventProvider.delete(this.event.id, this.event.CalendarEvent, isRecurring);
			this.navigationService.navigateTo(NavigationType.page, '/kalender');
		}
	}

	public async createEventType(): Promise<void> {
		void this.navigationService
			.navigateTo<{ eventType: IEventType }>(NavigationType.modal, EventTypeComponent, {
				action: Action.CREATE,
			})
			.then((result) => {
				if (result?.eventType) {
					this.eventTypes.push(result.eventType);
					this.eventForm.get('EventTypeId').setValue(result.eventType.id);
				}
				this.navigationDataManager.addData(NavigationDataKey.eventForm, this.eventForm);
			})
			.catch();
	}

	public async createLocation(): Promise<void> {
		await this.navigationService
			.navigateTo(NavigationType.modal, LocationComponent, {
				action: Action.CREATE,
			})
			.then((result) => {
				if (result?.location) {
					this.locations.push(result.location);
					this.eventForm.get('LocationId').setValue(result.location.id);
				}
				this.navigationDataManager.addData(NavigationDataKey.eventForm, this.eventForm);
			})
			.catch();
	}

	public async createDigibankAgency(): Promise<void> {
		void this.navigationService
			.navigateTo<{ digibankAgency: IEventType }>(NavigationType.modal, DigibankAgencyComponent, {
				action: Action.CREATE,
			})
			.then((result) => {
				if (result?.digibankAgency) {
					this.agencies.push(result.digibankAgency);
					this.eventForm.get('DigibankAgencyId').setValue(result.digibankAgency.id);
				}
				this.navigationDataManager.addData(NavigationDataKey.eventForm, this.eventForm);
			})
			.catch();
	}

	public toggleColorSelection(color: string): void {
		if (this.eventForm.get('color').value === color) {
			this.eventForm.get('color').setValue(null);
		} else {
			this.eventForm.get('color').setValue(color);
		}
	}

	public createOrEditLesson(lesson?: ILesson) {
		let lessonIndex: number = 0;
		if (lesson) {
			lessonIndex = this.eventForm.get('Lessons').value?.indexOf(lesson);
		}

		void this.navigationService
			.navigateTo<{ lesson: ILesson; resultingAction: Action }>(NavigationType.modal, LessonComponent, {
				lesson,
				lessonIndex,
				action: lesson ? Action.UPDATE : Action.CREATE,
			})
			.then((result) => {
				if (result?.lesson) {
					let lessons = this.eventForm.get('Lessons').value;
					switch (result.resultingAction) {
						case Action.CREATE:
							lessons = orderBy([...lessons, result.lesson], 'CalendarEvent.startsAt');
							break;

						case Action.UPDATE:
							lessons[lessonIndex] = result?.lesson;
							lessons = orderBy(lessons, 'CalendarEvent.startsAt');
							break;

						case Action.DELETE:
							lessons.splice(lessonIndex, 1);
							break;

						default:
					}

					this.eventForm.get('Lessons').setValue(lessons);
				}
				this.navigationDataManager.addData(NavigationDataKey.eventForm, this.eventForm);
			})
			.catch();
	}

	public close(): void {
		this.navigationService.navigateBack();
	}

	private async initializeForm(): Promise<void> {
		this.action = this.navigationService.getNavigationData(NavigationDataKey.action);
		this.event = this.navigationService.getNavigationData(NavigationDataKey.event);
		const state = this.navigationDataManager.getData<IFormGroup<IEventForm>>(NavigationDataKey.eventForm);
		this.selectedDate = this.navigationService.getNavigationData(NavigationDataKey.date);

		if (state) {
			this.eventForm = state;
		} else {
			this.eventForm = EventFormFactory.create(this.event, this.selectedDate);

			if (!this.event) {
				this.userDetailsManager.getUserDetails().subscribe((result: IUserDetails) => {
					this.eventForm.get('replyTo').patchValue(result.replyTo);
					this.eventForm.get('telephone').patchValue(result.telephone);
				});
			}
		}

		await this.getCustomCommunicationTemplates();
	}

	/**
	 * Get the locations from the API
	 */
	private async getLocations(): Promise<void> {
		this.locations = await this.locationProvider.getAll();
	}

	/**
	 * Get the eventTypes from the API
	 */
	private async getEventTypes(): Promise<void> {
		this.eventTypes = await this.eventTypeProvider.getAll();
	}

	/**
	 * Get the employees from the API
	 */
	private async getEmployees(): Promise<void> {
		this.employees = await this.employeeProvider.getAllForEvent();
	}

	/**
	 * Get the digibank agencies from the API
	 */
	private async getDigibankAgencies(): Promise<void> {
		this.agencies = await this.digibankAgencyProvider.getAll();
	}

	/**
	 * Get the uit database labels from the API
	 */
	private async getUitDatabaseLabels(): Promise<void> {
		this.uitDatabaseLabels = await this.uitDatabaseLabelProvider.getAll();
	}

	/**
	 * Get the event attendance types based on the {@link EventAttendanceType} enum
	 * formatted for the form-select
	 *
	 * We filter the attendance types to make sure when editing a lesson, you can't change to another attendance type,
	 * and when editing a non-lesson, you cannot change to lesson.
	 */
	private getEventAttendanceTypes(): void {
		this.eventAttendanceTypes = Object.keys(EventAttendanceType)
			.filter((key) => {
				if (this.action === Action.UPDATE) {
					if (this.event.attendanceType === EventAttendanceType.Lessons) {
						return EventAttendanceType[key] === EventAttendanceType.Lessons;
					}
					return EventAttendanceType[key] !== EventAttendanceType.Lessons;
				}
				return true;
			})
			.map((key) => ({
				value: EventAttendanceType[key],
				label: translate(`EVENT_ATTENDANCE_TYPE.${key.toUpperCase()}`),
			}));
	}

	/**
	 * Get the IsLoanLocationOptions options
	 * formatted for the form-select
	 */
	private getIsLoanLocationOptions(): void {
		this.isLoanLocationOptions = [
			{ value: true, label: translate('YES') },
			{ value: false, label: translate('NO') },
		];
	}

	/**
	 * Get the getDigibankActivities options
	 * formatted for the form-select
	 */
	private getDigibankActivities(): void {
		this.digibankActivities = Object.keys(DigibankActivity)
			.filter((key) => DigibankActivity[key] !== DigibankActivity.Loan)
			.map((key) => ({
				value: DigibankActivity[key],
				label: translate(`EVENT.DIGIBANK_ACTIVITY.${key.toUpperCase()}`),
			}));
	}

	/**
	 * Get the getDigibankObjectives options
	 * formatted for the form-select
	 */
	private getDigibankObjectives(): void {
		this.digibankObjectives = Object.keys(DigibankObjective)
			.filter((key) => DigibankObjective[key] !== DigibankObjective.Loan)
			.map((key) => ({
				value: DigibankObjective[key],
				label: translate(`EVENT.DIGIBANK_OBJECTIVE.${key.toUpperCase()}`),
			}));
	}

	/**
	 * Get the custom interval types based on the {@link CustomIntervalType} enum
	 * formatted for the form-select
	 */
	private getCustomIntervalTypes(): void {
		this.customIntervalTypes = Object.keys(CustomIntervalType).map((key) => ({
			value: CustomIntervalType[key],
			label: translate(`EVENT_INTERVAL.CUSTOM_INTERVAL.${key.toUpperCase()}`),
		}));
	}

	/**
	 * Get the custom interval types based on the {@link EventLocationType} enum
	 * formatted for the form-select
	 */
	private getEventLocationTypes(): void {
		this.eventLocationTypes = Object.keys(EventLocationType).map((key) => ({
			value: EventLocationType[key],
			label: translate(`EVENT_LOCATION_TYPE.${key.toUpperCase()}`),
		}));
	}

	/**
	 * Get the event intervals based on the {@link EventInterval} enum
	 * formatted for the form-select
	 */
	private getEventIntervals(): void {
		const selectedDate = this.selectedDate
			? new Date(this.selectedDate)
			: this.event?.CalendarEvent.startsAt
				? new Date(this.event.CalendarEvent.startsAt)
				: new Date();
		const date = format(selectedDate, 'do', { weekStartsOn: 1, locale: nlBE });
		const day = `${this.getOccurrenceOfWeekdayInMonthAsString(selectedDate)} ${format(selectedDate, 'eeee', {
			weekStartsOn: 1,
			locale: nlBE,
		})}`;

		this.eventIntervals = Object.keys(EventInterval).map((key) => ({
			value: EventInterval[key],
			label: translate(`EVENT_INTERVAL.${key.toUpperCase()}`, { date, day }),
		}));
	}

	/**
	 * Get the occurrence of a weekday in a month (ex. the 2nd monday of the month)
	 * formatted as string using the application locale
	 * @param date The date to get the occurrence for
	 * @returns The occurrence as formatted string
	 */
	private getOccurrenceOfWeekdayInMonthAsString(date: Date): string {
		const dayNumber = getDate(date);
		const occurrence = Math.floor(dayNumber / 7 - 0.00001) + 1;
		return format(set(new Date(), { date: occurrence }), 'do', { weekStartsOn: 1, locale: nlBE });
	}

	/**
	 * Get all the possible custom communication templates for a message type
	 */
	private async getCustomCommunicationTemplates(): Promise<void> {
		const getCustomCommunicationTemplates = async (value: EventAttendanceType) => {
			if (value !== EventAttendanceType.WalkIn) {
				this.customCommunicationTemplates = await this.communicationTemplateProvider.getAllForCustomMessageType(
					value === EventAttendanceType.Lessons
						? CommunicationMessageType.CustomLessonReservation
						: CommunicationMessageType.CustomReservation
				);
			} else {
				this.customCommunicationTemplates = [];
			}
		};

		if (this.eventForm.get('attendanceType').value) {
			getCustomCommunicationTemplates(this.eventForm.get('attendanceType').value);
		}

		this.eventForm.get('attendanceType').valueChanges.subscribe((value) => {
			getCustomCommunicationTemplates(value);
		});
	}
}
